import { addDays, isValid } from 'date-fns';

/** Dates as the API sends and accepts them: DD-MM-YYYY. */
export type ApiDate = string;

export interface DateRange {
  start: ApiDate;
  end: ApiDate;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Parses DD-MM-YYYY, DD.MM.YYYY or YYYY-MM-DD into a local date (null if invalid). */
export function parseApiDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parts = value.trim().split(/[-.]/);
  if (parts.length !== 3) return null;
  const [a = '', b = '', c = ''] = parts;
  const [y, m, d] = a.length === 4 ? [+a, +b, +c] : [+c, +b, +a];
  const date = new Date(y, m - 1, d);
  if (
    !isValid(date) ||
    date.getFullYear() !== y ||
    date.getMonth() !== m - 1 ||
    date.getDate() !== d
  ) {
    return null;
  }
  return date;
}

/** Date → DD-MM-YYYY (the API format). */
export const toApiDate = (date: Date): ApiDate =>
  `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`;

/** DD-MM-YYYY → DD.MM.YYYY for display. */
export const displayDate = (value: ApiDate | null | undefined) =>
  value ? value.replaceAll('-', '.') : '';

export const formatRange = (range: DateRange | null) => {
  if (!range) return '';
  const start = displayDate(range.start);
  const end = displayDate(range.end);
  return start === end ? start : `${start} – ${end}`;
};

export const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

/** Metrics exist only for past days; today and future dates are not selectable. */
export const isPastDate = (value: ApiDate) => {
  const d = parseApiDate(value);
  return !!d && d.getTime() < startOfToday().getTime();
};

/**
 * Production default: yesterday if it has data, otherwise the most recent available day before
 * today.
 */
export function defaultRangeDate(available: ApiDate[]): ApiDate | null {
  const past = available.filter(isPastDate);
  if (past.length === 0) return null;
  const yesterday = toApiDate(addDays(startOfToday(), -1));
  if (past.includes(yesterday)) return yesterday;
  return (
    [...past].sort(
      (x, y) => (parseApiDate(y)?.getTime() ?? 0) - (parseApiDate(x)?.getTime() ?? 0),
    )[0] ?? null
  );
}

export const isValidRange = (range: DateRange) => {
  const s = parseApiDate(range.start);
  const e = parseApiDate(range.end);
  return !!s && !!e && s.getTime() <= e.getTime();
};
